import { lazy, ComponentType } from "react";
import Bugsnag from "@bugsnag/js";
import { isTrackingDisabled } from "src/utils/tracking";

const RELOAD_FLAG_PREFIX = "protectedLazy.reloaded:";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(() => resolve(), ms));

// Only a failed *fetch* of the chunk is worth retrying. An error thrown while
// the module evaluates is a real bug, and reloading on it would bury that bug
// under a refresh loop, so match narrowly and rethrow anything else. Note this
// has to be a positive check: `e.name !== "ChunkLoadError"` lets a thrown
// non-Error fall through into the retry path rather than out of it.
const isChunkLoadError = (e: unknown) =>
  e instanceof Error &&
  (e.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk \S+ failed/i.test(e.message) ||
    /Failed to fetch dynamically imported module/i.test(e.message));

// Claims the single reload each chunk is allowed per tab. Returning a pending
// promise stops the loop within one page load, but nothing carries across the
// reload itself, so an asset that is genuinely gone rather than stale would
// reload the tab again on every load. sessionStorage throws rather than no-ops
// in some hardened privacy modes; when we cannot record that a reload happened
// we decline it instead, and let the error boundary offer a manual retry.
const claimReload = (key: string) => {
  try {
    if (window.sessionStorage.getItem(key) !== null) {
      return false;
    }
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
};

const releaseReload = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {} // eslint-disable-line no-empty
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const protectedLazy = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) => {
  const reloadKey = `${RELOAD_FLAG_PREFIX}${factory.toString()}`;

  const attempt = async (firstAttempt = true): Promise<{ default: T }> => {
    try {
      const module = await factory();
      releaseReload(reloadKey);
      return module;
    } catch (e) {
      if (!isChunkLoadError(e)) {
        throw e;
      }

      await sleep(1000);

      if (!firstAttempt) {
        // A deploy replaces the hashed chunk filenames the loaded HTML points
        // at, so this tab is asking for a file that no longer exists. Reloading
        // picks up the current index.html and its current chunk names.
        if (claimReload(reloadKey)) {
          window.location.reload();
          // reload is async, don't keep refetching a chunk that is gone
          return new Promise<{ default: T }>(() => undefined);
        }

        // We already reloaded for this chunk and it still will not load, so it
        // is unreachable rather than stale. Report it and let the error
        // boundary render instead of reloading again.
        if (!isTrackingDisabled()) {
          Bugsnag.notify(e as Error, (event) => {
            event.addMetadata("protectedLazy", { chunk: factory.toString() });
          });
        }
        throw e;
      }

      return attempt(false);
    }
  };

  return lazy(() => attempt());
};
